import { useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { Eye, FileText } from "lucide-react";
import moment from "moment-timezone";
import { dateSort, dateWithFormatSort, DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { Button } from "@/components/ui/button";
import { BrokerageFile, useGetBrokerageDocumentLink } from "@/lib/api/brokerage";
import { CommonDestinationTimezone } from "@/lib/utils";
import { toast } from "@/components/ui/use-toast";
import { Loader } from "@/components/ui/loader";
import { PDFViewerDialog } from "../components/PDFViewerDialog";
import { UpdateBrokerageFileModal } from "../components/UpdateBrokerageFileModal";
import { useQueryParam } from "@/hooks/use-query-param";

interface BrokerageFilesTableProps {
  files: BrokerageFile[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  currentPage: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

export function BrokerageFilesTable({
  files,
  pagination,
  currentPage,
  onPageChange,
  isLoading,
}: BrokerageFilesTableProps) {
  const [loadingFiles, setLoadingFiles] = useState<Set<string>>(new Set());
  const [selectedFile, setSelectedFile] = useState<BrokerageFile | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fileId, setFileId] = useQueryParam("file_id", "");
  const getDocumentLink = useGetBrokerageDocumentLink();

  const handleOpenInNewTab = async (file: BrokerageFile) => {
    if (loadingFiles.has(file.id)) { return; }
    setLoadingFiles(prev => new Set(prev).add(file.id));
    
    try {
      const result = await getDocumentLink(file.id);
      
      if (result.success && result.data) {
        window.open(result.data.link, '_blank');
      } else {
        toast({
          title: "Error",
          description: "Failed to open document. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while opening the document.",
        variant: "destructive",
      });
    } finally {
      setLoadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(file.id);
        return newSet;
      });
    }
  };

  const handleOpenInDialog = (file: BrokerageFile) => {
    setSelectedFile(file);
    setDialogOpen(true);
  };

  const handleCellClick = (row: BrokerageFile) => {
    setFileId(row.id);
  };

  const columns: DataTableColumn<BrokerageFile>[] = [
    {
      id: "filename",
      accessorKey: "filename",
      header: "Filename",
      enableSorting: true,
      accessorFn: (row: any) => row.name || row.Document?.filename || "",
      cell: (info) => {
        const displayName = info.getValue() as any  || "";
        return (
          <div className="font-medium">
            {displayName}
          </div>
        );
      },
    },
    {
      id: "request_date",
      accessorKey: "request_date",
      header: "Date",
      sortingFn: dateWithFormatSort("MMM DD, YYYY"),
        accessorFn: (row: any) => row.request_date ? moment.tz(row.request_date, CommonDestinationTimezone).format("MMM DD, YYYY") : "-",
        cell: (info) => {
          return info.getValue() as string;
      },
    } as DataTableColumn<BrokerageFile>,
    {
      id: "statement_type",
      accessorKey: "statement_type",
      header: "Type",
      accessorFn: (row: any) => {
         switch(row.statement_type || "") {
            case "monthly_statement":
              return "Monthly";
            case "daily_statement":
              return "Daily";
            case "notice_statement":
              return "Notice";
            default:
              return "";
        }
      },
      cell: (info) => info.getValue() as string,
    },
    {
      id: "actions",
      header: "Actions",
      disableRowClick: true,
      cell: ({ row }) => {
        const file = row.original;
        const isLoading = loadingFiles.has(file.id);
        
        return (
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleOpenInNewTab(file)}
              disabled={isLoading}
              title="Open in new tab"
            >
              {isLoading ? (
                <Loader svgClassName="w-4 h-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleOpenInDialog(file)}
              title="View in dialog"
            >
              <FileText className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];
  
  return (
    <div className="space-y-4">
          <DataTable
              className="shadow-lg"
              tableClassName={"max-h-[800px] overflow-y-auto"}
              data={files}
              columns={columns}
              stickyHeaders
              enableColumnVisibility
              enableSorting
              enableMultiSort
              enableFiltering
              enableGrouping
              virtualizationRowHeight={57}
              virtualizationOverscan={20}
              enableVirtualization
              enableExport
              filterStyle='select'
              filterMode='internal'
              emptyMessage={isLoading ? "Loading...": "No dorman files found"}
              defaultSorting={[{ id: "request_date", desc: true }, { id: "filename", desc: false }]}
              rowClickable
              onRowClick={handleCellClick}
          />


          
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <Button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                variant="outline"
              >
                Previous
              </Button>
              
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {pagination.totalPages} | Total: {pagination.total} files
              </span>
              
              <Button
                onClick={() => onPageChange(Math.min(pagination.totalPages, currentPage + 1))}
                disabled={currentPage === pagination.totalPages}
                variant="outline"
              >
                Next
              </Button>
            </div>
          )}

      <PDFViewerDialog
        file={selectedFile}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
      
      <UpdateBrokerageFileModal files={files} />
    </div>
  );
}
